import { useEffect, useRef, useState } from "react";
import type { LinkItem, LinksPage } from "../../shared/links";
import { linksPath } from "../lib/apiPaths";

async function fetchLinksPage(
  search: string,
  cursor: string | null,
  signal?: AbortSignal
): Promise<LinksPage> {
  const params = new URLSearchParams(search);
  if (cursor) params.set("cursor", cursor);
  const qs = params.toString();
  const res = await fetch(`${linksPath()}${qs ? `?${qs}` : ""}`, {
    credentials: "include",
    signal,
  });
  if (!res.ok) throw new Error(`Loading links failed with ${res.status}`);
  return res.json();
}

type Pages = {
  search: string;
  items: LinkItem[];
  nextCursor: string | null;
  total: number;
};

// Pages through /links for one set of filters; changing `search` starts over.
export function useLinkPages(search: string) {
  const [pages, setPages] = useState<Pages | null>(null);
  const [error, setError] = useState<unknown>(null);
  const loadingCursor = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchLinksPage(search, null, controller.signal).then(
      (page) =>
        setPages({
          search,
          items: page.items,
          nextCursor: page.nextCursor,
          total: page.total ?? 0,
        }),
      (err: unknown) => {
        if (!controller.signal.aborted) setError(err);
      }
    );
    return () => controller.abort();
  }, [search]);

  // Rethrown during render so the error boundary shows it and offers a retry.
  if (error) throw error;

  // Until the new search's first page lands, keep showing the previous
  // results rather than blanking the list.
  const current = pages?.search === search ? pages : null;

  const loadMore = () => {
    const cursor = current?.nextCursor;
    if (!cursor || loadingCursor.current === cursor) return;
    loadingCursor.current = cursor;
    void fetchLinksPage(search, cursor).then(
      (page) =>
        setPages((prev) =>
          prev?.search === search && prev.nextCursor === cursor
            ? {
                ...prev,
                items: [...prev.items, ...page.items],
                nextCursor: page.nextCursor,
              }
            : prev
        ),
      (err: unknown) => {
        loadingCursor.current = null;
        throw err;
      }
    );
  };

  return {
    items: pages?.items ?? [],
    total: pages?.total ?? null,
    hasMore: current?.nextCursor != null,
    loading: pages === null,
    loadMore,
  };
}
